import { useState, useMemo, useCallback } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import type { Bot } from '@/types/bot';
import { useLiveBots } from '@/hooks/useLiveBots';
import { useMarketData } from '@/hooks/useMarketData';
import { useForexRates } from '@/contexts/ForexRatesContext';
import { useQuotes } from '@/hooks/useQuotes';
import TopBar from '@/components/layout/TopBar';
import MarketNews from '@/components/news/MarketNews';
import { StatusDot } from '@/components/shared/StatusDot';
import { MfaDialog } from '@/components/shared/MfaDialog';
import './Terminal.css';

// ----- Sort helpers -----
type SortKey =
  | 'status'
  | 'name'
  | 'symbol'
  | 'broker'
  | 'market'
  | 'strategyGroup'
  | 'mode'
  | 'pnl'
  | 'positions'
  | 'fills'
  | 'winRate';

type SortDir = 'asc' | 'desc';

function getSortValue(bot: Bot, key: SortKey): string | number {
  switch (key) {
    case 'status':
      return bot.status;
    case 'name':
      return bot.name.toLowerCase();
    case 'symbol':
      return bot.symbol.toLowerCase();
    case 'broker':
      return bot.broker;
    case 'market':
      return bot.market;
    case 'strategyGroup':
      return bot.strategyGroup;
    case 'mode':
      return bot.mode;
    case 'pnl':
      return bot.performance?.todayPnl ?? 0;
    case 'positions':
      return bot.performance?.activePositions ?? 0;
    case 'fills':
      return bot.performance?.fills ?? 0;
    case 'winRate':
      return bot.performance?.winRate ?? 0;
  }
}

function sortBots(bots: Bot[], key: SortKey, dir: SortDir): Bot[] {
  return [...bots].sort((a, b) => {
    const va = getSortValue(a, key);
    const vb = getSortValue(b, key);
    if (va < vb) return dir === 'asc' ? -1 : 1;
    if (va > vb) return dir === 'asc' ? 1 : -1;
    return 0;
  });
}

// ----- Mock PnL chart data -----
function generatePnlData(): { hour: string; pnl: number }[] {
  const data: { hour: string; pnl: number }[] = [];
  let cumulative = 0;
  for (let h = 0; h < 24; h++) {
    const delta = (Math.random() - 0.45) * 120;
    cumulative += delta;
    data.push({
      hour: `${h.toString().padStart(2, '0')}:00`,
      pnl: Math.round(cumulative * 100) / 100,
    });
  }
  return data;
}

// ----- AUD conversion -----
function computeTotalPnlAud(bots: Bot[], rates: Record<string, number>): number {
  return bots.reduce((sum, bot) => {
    const pnl = bot.performance?.todayPnl || 0;
    const rate = rates[bot.market] || 1;
    return sum + pnl * rate;
  }, 0);
}

// ----- Format helpers -----
function fmtPnl(v: number): string {
  const sign = v >= 0 ? '+' : '';
  return `${sign}${v.toFixed(2)}`;
}

function fmtPct(v: number): string {
  return `${v.toFixed(1)}%`;
}

function fmtTime(iso?: string): string {
  if (!iso) return '--';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return iso;
  }
}

// ===== Main Component =====
export default function Terminal() {
  const { bots, startBot, stopBot } = useLiveBots();
  const { indices, infraHealth } = useMarketData();
  const { rates } = useForexRates();
  const { quotes } = useQuotes();

  const totalPnlAud = useMemo(() => computeTotalPnlAud(bots, rates), [bots, rates]);
  const syncStatus = infraHealth.syncStatus;

  // Sort state
  const [sortKey, setSortKey] = useState<SortKey>('pnl');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const sortedBots = useMemo(() => sortBots(bots, sortKey, sortDir), [bots, sortKey, sortDir]);

  const handleSort = useCallback(
    (key: SortKey) => {
      if (sortKey === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortKey(key);
        setSortDir(key === 'pnl' || key === 'fills' || key === 'winRate' ? 'desc' : 'asc');
      }
    },
    [sortKey],
  );

  // MFA dialog state
  const [mfaBot, setMfaBot] = useState<{ id: string; name: string } | null>(null);

  const handleStartBot = useCallback((bot: { id: string; name: string; broker: string }) => {
    if (bot.broker === 'prostocks') {
      setMfaBot({ id: bot.id, name: bot.name });
    } else {
      startBot(bot.id);
    }
  }, [startBot]);

  // Selected bot
  const [selectedBotId, setSelectedBotId] = useState<string | null>(null);
  const selectedBot = useMemo(
    () => bots.find((b) => b.id === selectedBotId) ?? null,
    [bots, selectedBotId],
  );

  // PnL chart data (generated once)
  const [pnlData] = useState(() => generatePnlData());

  // Column header renderer
  const renderTh = (key: SortKey, label: string, className: string) => (
    <th
      className={`${className}${sortKey === key ? ' th--sorted' : ''}`}
      onClick={() => handleSort(key)}
    >
      {label}
      {sortKey === key && (
        <span className="sort-arrow">{sortDir === 'asc' ? '\u25B2' : '\u25BC'}</span>
      )}
    </th>
  );

  return (
    <div className="terminal">
      <TopBar totalPnlAud={totalPnlAud} syncStatus={syncStatus} indices={indices} />

      <div className="terminal__body">
        {/* === TOP ROW === */}
        <div className="terminal__top-row">
          {/* Bot Table */}
          <div className="terminal__panel">
            <div className="terminal__panel-header">
              <span className="terminal__panel-title">All Bots</span>
              <span className="terminal__panel-badge">{bots.length} total</span>
            </div>
            <div className="terminal__table-wrap">
              <table className="terminal__table">
                <thead>
                  <tr>
                    {renderTh('status', '', 'col-status')}
                    {renderTh('name', 'Name', 'col-name')}
                    {renderTh('symbol', 'Symbol', 'col-symbol')}
                    {renderTh('broker', 'Broker', 'col-broker')}
                    {renderTh('market', 'Mkt', 'col-market')}
                    {renderTh('strategyGroup', 'Strategy', 'col-strategy')}
                    {renderTh('mode', 'Mode', 'col-mode')}
                    {renderTh('pnl', 'P&L', 'col-pnl')}
                    {renderTh('positions', 'Pos', 'col-positions')}
                    {renderTh('fills', 'Fills', 'col-fills')}
                    {renderTh('winRate', 'Win%', 'col-winrate')}
                    <th className="col-actions">Act</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedBots.map((bot) => {
                    const pnl = bot.performance?.todayPnl ?? 0;
                    const isStopped = bot.status === 'stopped';
                    const isSelected = bot.id === selectedBotId;

                    return (
                      <tr
                        key={bot.id}
                        className={`${isSelected ? 'tr--selected' : ''}${isStopped ? ' tr--stopped' : ''}`}
                        onClick={() => setSelectedBotId(bot.id)}
                      >
                        <td>
                          <StatusDot status={bot.status} />
                        </td>
                        <td title={bot.name}>{bot.name}</td>
                        <td className="terminal__mono">{bot.symbol}</td>
                        <td>{bot.broker}</td>
                        <td>{bot.market}</td>
                        <td title={bot.strategyGroup}>{bot.strategyGroup}</td>
                        <td>{bot.mode}</td>
                        <td
                          className={`terminal__mono ${pnl >= 0 ? 'terminal__pnl--positive' : 'terminal__pnl--negative'}`}
                        >
                          {fmtPnl(pnl)}
                        </td>
                        <td className="terminal__mono">
                          {bot.performance?.activePositions ?? 0}
                        </td>
                        <td className="terminal__mono">{bot.performance?.fills ?? 0}</td>
                        <td className="terminal__mono">
                          {fmtPct(bot.performance?.winRate ?? 0)}
                        </td>
                        <td>
                          {bot.status === 'active' || bot.status === 'paused' ? (
                            <button
                              className="terminal__action-btn terminal__action-btn--stop"
                              onClick={(e) => {
                                e.stopPropagation();
                                stopBot(bot.id);
                              }}
                            >
                              Stop
                            </button>
                          ) : (
                            <button
                              className="terminal__action-btn terminal__action-btn--start"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStartBot(bot);
                              }}
                            >
                              Start
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Detail Panel */}
          <div className="terminal__panel">
            <div className="terminal__panel-header">
              <span className="terminal__panel-title">Bot Detail</span>
              {selectedBot && (
                <span className="terminal__panel-badge">{selectedBot.symbol}</span>
              )}
            </div>
            <div className="terminal__panel-content">
              {!selectedBot ? (
                <div className="terminal__detail-empty">Select a bot from the table</div>
              ) : (
                <div className="terminal__detail">
                  <div className="terminal__detail-header">
                    <div>
                      <span className="terminal__detail-name">{selectedBot.name}</span>
                      <span className="terminal__detail-symbol">{selectedBot.symbol}</span>
                    </div>
                    {selectedBot.status === 'active' || selectedBot.status === 'paused' ? (
                      <button
                        className="terminal__action-btn terminal__action-btn--stop"
                        onClick={() => stopBot(selectedBot.id)}
                      >
                        Stop Bot
                      </button>
                    ) : (
                      <button
                        className="terminal__action-btn terminal__action-btn--start"
                        onClick={() => handleStartBot(selectedBot)}
                      >
                        Start Bot
                      </button>
                    )}
                  </div>

                  {/* Live Quote */}
                  {quotes[selectedBot.symbol] && (() => {
                    const q = quotes[selectedBot.symbol];
                    const isUp = q.change >= 0;
                    return (
                      <div className="terminal__quote">
                        <span className="terminal__quote-price">{q.price.toFixed(2)}</span>
                        <span className={`terminal__quote-change ${isUp ? 'terminal__pnl--positive' : 'terminal__pnl--negative'}`}>
                          {isUp ? '+' : ''}{q.change.toFixed(2)} ({isUp ? '+' : ''}{q.changePercent.toFixed(2)}%)
                        </span>
                        <div className="terminal__quote-range">
                          <span className="terminal__detail-label">L {q.low.toFixed(2)}</span>
                          <span className="terminal__detail-label">O {q.open.toFixed(2)}</span>
                          <span className="terminal__detail-label">H {q.high.toFixed(2)}</span>
                        </div>
                      </div>
                    );
                  })()}

                  <div className="terminal__detail-grid">
                    <div className="terminal__detail-item">
                      <span className="terminal__detail-label">Status</span>
                      <span className="terminal__detail-value" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <StatusDot status={selectedBot.status} />
                        {selectedBot.status.toUpperCase()}
                      </span>
                    </div>
                    <div className="terminal__detail-item">
                      <span className="terminal__detail-label">Mode</span>
                      <span className="terminal__detail-value">{selectedBot.mode.toUpperCase()}</span>
                    </div>
                    <div className="terminal__detail-item">
                      <span className="terminal__detail-label">Broker</span>
                      <span className="terminal__detail-value">{selectedBot.broker.toUpperCase()}</span>
                    </div>
                    <div className="terminal__detail-item">
                      <span className="terminal__detail-label">Market</span>
                      <span className="terminal__detail-value">{selectedBot.market}</span>
                    </div>
                    <div className="terminal__detail-item">
                      <span className="terminal__detail-label">Tier</span>
                      <span className="terminal__detail-value">{selectedBot.tier.toUpperCase()}</span>
                    </div>
                    <div className="terminal__detail-item">
                      <span className="terminal__detail-label">Strategy</span>
                      <span className="terminal__detail-value">{selectedBot.strategyGroup}</span>
                    </div>
                  </div>

                  {/* Performance */}
                  <div className="terminal__detail-section">
                    <div className="terminal__detail-section-title">Performance</div>
                    <div className="terminal__detail-grid">
                      <div className="terminal__detail-item">
                        <span className="terminal__detail-label">Today P&L</span>
                        <span
                          className={`terminal__detail-value ${(selectedBot.performance?.todayPnl ?? 0) >= 0 ? 'terminal__pnl--positive' : 'terminal__pnl--negative'}`}
                        >
                          {fmtPnl(selectedBot.performance?.todayPnl ?? 0)}
                        </span>
                      </div>
                      <div className="terminal__detail-item">
                        <span className="terminal__detail-label">Total P&L</span>
                        <span
                          className={`terminal__detail-value ${(selectedBot.performance?.totalPnl ?? 0) >= 0 ? 'terminal__pnl--positive' : 'terminal__pnl--negative'}`}
                        >
                          {fmtPnl(selectedBot.performance?.totalPnl ?? 0)}
                        </span>
                      </div>
                      <div className="terminal__detail-item">
                        <span className="terminal__detail-label">Win Rate</span>
                        <span className="terminal__detail-value">
                          {fmtPct(selectedBot.performance?.winRate ?? 0)}
                        </span>
                      </div>
                      <div className="terminal__detail-item">
                        <span className="terminal__detail-label">Total Trades</span>
                        <span className="terminal__detail-value">
                          {selectedBot.performance?.totalTrades ?? 0}
                        </span>
                      </div>
                      <div className="terminal__detail-item">
                        <span className="terminal__detail-label">Active Positions</span>
                        <span className="terminal__detail-value">
                          {selectedBot.performance?.activePositions ?? 0}
                        </span>
                      </div>
                      <div className="terminal__detail-item">
                        <span className="terminal__detail-label">Fills</span>
                        <span className="terminal__detail-value">
                          {selectedBot.performance?.fills ?? 0}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Strategy Params */}
                  <div className="terminal__detail-section">
                    <div className="terminal__detail-section-title">Strategy Parameters</div>
                    <div className="terminal__detail-grid">
                      <div className="terminal__detail-item">
                        <span className="terminal__detail-label">Buy Threshold</span>
                        <span className="terminal__detail-value">
                          {selectedBot.strategy?.buyThreshold ?? '--'}
                        </span>
                      </div>
                      <div className="terminal__detail-item">
                        <span className="terminal__detail-label">Sell Threshold</span>
                        <span className="terminal__detail-value">
                          {selectedBot.strategy?.sellThreshold ?? '--'}
                        </span>
                      </div>
                      <div className="terminal__detail-item">
                        <span className="terminal__detail-label">Position Size</span>
                        <span className="terminal__detail-value">
                          {selectedBot.strategy?.positionSize ?? '--'}
                        </span>
                      </div>
                      <div className="terminal__detail-item">
                        <span className="terminal__detail-label">Max Positions</span>
                        <span className="terminal__detail-value">
                          {selectedBot.strategy?.maxPositions ?? '--'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Timestamps */}
                  <div className="terminal__detail-section">
                    <div className="terminal__detail-section-title">Timestamps</div>
                    <div className="terminal__detail-grid">
                      <div className="terminal__detail-item">
                        <span className="terminal__detail-label">Last Update</span>
                        <span className="terminal__detail-value">{fmtTime(selectedBot.lastUpdate)}</span>
                      </div>
                      <div className="terminal__detail-item">
                        <span className="terminal__detail-label">Created</span>
                        <span className="terminal__detail-value">{fmtTime(selectedBot.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* === BOTTOM ROW === */}
        <div className="terminal__bottom-row">
          {/* PnL Chart */}
          <div className="terminal__panel">
            <div className="terminal__panel-header">
              <span className="terminal__panel-title">P&L Curve (Today)</span>
            </div>
            <div className="terminal__chart-wrap">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={pnlData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="pnlGreen" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10B981" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="pnlRed" x1="0" y1="1" x2="0" y2="0">
                      <stop offset="0%" stopColor="#EF4444" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#EF4444" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="hour"
                    tick={{ fill: '#6B6B88', fontSize: 10, fontFamily: 'var(--font-mono)' }}
                    axisLine={{ stroke: '#2A2A44' }}
                    tickLine={false}
                    interval={3}
                  />
                  <YAxis
                    tick={{ fill: '#6B6B88', fontSize: 10, fontFamily: 'var(--font-mono)' }}
                    axisLine={false}
                    tickLine={false}
                    width={50}
                  />
                  <Tooltip
                    contentStyle={{
                      background: '#121224',
                      border: '1px solid #2A2A44',
                      borderRadius: 4,
                      fontSize: 12,
                      fontFamily: 'var(--font-mono)',
                    }}
                    labelStyle={{ color: '#B0B0C8' }}
                    itemStyle={{ color: '#FFFFFF' }}
                  />
                  <ReferenceLine y={0} stroke="#2A2A44" strokeDasharray="3 3" />
                  <Area
                    type="monotone"
                    dataKey="pnl"
                    stroke="#10B981"
                    fill="url(#pnlGreen)"
                    strokeWidth={1.5}
                    dot={false}
                    activeDot={{ r: 3, fill: '#10B981' }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Market News & Research */}
          <div className="terminal__panel">
            <div className="terminal__panel-header">
              <span className="terminal__panel-title">Market News</span>
              <a href="/audit" className="terminal__panel-link">View Audit</a>
            </div>
            <div className="terminal__panel-content">
              <MarketNews limit={15} />
            </div>
          </div>
        </div>
      </div>

      {mfaBot && (
        <MfaDialog
          botName={mfaBot.name}
          onSubmit={(otp) => { startBot(mfaBot.id, otp); setMfaBot(null); }}
          onCancel={() => setMfaBot(null)}
        />
      )}
    </div>
  );
}
